/**
 * SWMSTab.js
 * SWMS (Safe Work Method Statement) tab for ProjectDetail.
 *
 * Data flow:
 *   - Derived project info  → read from `project` prop (no extra storage)
 *   - Company info          → fetched from GET /api/settings
 *   - SWMS-specific data    → fetched/saved via GET|PATCH /api/swms/{project.id}
 *
 * Autosave: debounce 1 s on any field change → PATCH /api/swms/{project.id}
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';  // interceptor auto-injects Bearer token (set up in auth.js)
import { isAdmin } from '../../lib/auth';
import ChapterCover from './swms/ChapterCover';
import ChapterContent from './swms/ChapterContent';
import Chapter001 from './swms/Chapter001';
import Chapter002 from './swms/Chapter002';
import Chapter004 from './swms/Chapter004';
import Chapter005 from './swms/Chapter005';
import Chapter006 from './swms/Chapter006';
import Chapter007 from './swms/Chapter007';
import Chapter009 from './swms/Chapter009';
import Chapter011 from './swms/Chapter011';
import Chapter003 from './swms/Chapter003';
import Chapter008 from './swms/Chapter008';
import Chapter010 from './swms/Chapter010';
import Chapter012 from './swms/Chapter012';
import Chapter013 from './swms/Chapter013';
import Chapter014 from './swms/Chapter014';
import Chapter019 from './swms/Chapter019';
import Chapter020 from './swms/Chapter020';
import Chapter021 from './swms/Chapter021';
import Chapter022 from './swms/Chapter022';
import Chapter023 from './swms/Chapter023';
import Chapter025 from './swms/Chapter025';
import Chapter026 from './swms/Chapter026';
import Chapter027 from './swms/Chapter027';
import Chapter015 from './swms/Chapter015';
import Chapter016 from './swms/Chapter016';
import Chapter017 from './swms/Chapter017';
import Chapter018 from './swms/Chapter018';
import Chapter031 from './swms/Chapter031';
import Chapter024 from './swms/Chapter024';
import Chapter028 from './swms/Chapter028';
import Chapter029 from './swms/Chapter029';
import Chapter030 from './swms/Chapter030';
import SwmsPdfExportModal from './swms/SwmsPdfExportModal';
import SwmsPdfPreview from './swms/SwmsPdfPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_SWMS = {
  scope_of_works: '',
  principal_contractor: '',
  site_manager_name: '',
  site_manager_phone: '',
  site_manager_email: '',
  number_of_workers: 0,
  company_fax: '',
  company_signature_url: '',
  doc_version: '1.0',
  doc_review_date: '',
  doc_next_review: '',
  doc_author: '',
  doc_approver: '',
  doc_approval_date: '',
  revision_history: [],
  distribution_list: [],
  hazard_identifications: [],
  hazard_categories: {},
  swms_steps: [],
  ppe: {},
  training_entries: [],
  toolbox_talks: [],
  workplace_inspections: [],
  equipment_entries: [],
  equipment_prestart_check: {},
  equipment_periodic_check: {},
  ohse_policy: { policy_date: '', signed_by: '', signatory_title: '', signature: '' },
  objectives_targets: [],
  roles_responsibilities: [],
  employee_responsibilities: [],
  hazardous_substance_register: [],
  electrical_equipment_register: [],
  incident_entries: [],
  injury_register: [],
  incident_investigations: [],
  site_rules: [],
  emergency_contacts: [],
  hazard_reports: [],
  ohse_checklist: {},
  injury_management_policy: {},
  signin_entries: [],
  nearest_hospital: '',
  assembly_point: '',
};

// ── Main component ────────────────────────────────────────────────────────────

export default function SWMSTab({ project, onProjectChange }) {
  const [swmsData, setSwmsData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showPdfExport, setShowPdfExport] = useState(false);
  const saveTimer = useRef(null);
  const pendingPatch = useRef({});

  // Derived project info — no extra storage needed
  const derivedProject = {
    project_name: project?.project_name || '',
    project_address: project?.site_address || '',
    project_start_date: project?.start_date || '',
    project_end_date: project?.end_date || '',
    client_name: project?.client_name || '',
    state: project?.state || 'NSW',
    project_type: project?.project_type || 'Retail',
  };

  // Load SWMS document and company settings on mount
  useEffect(() => {
    if (!project?.id) return;

    const load = async () => {
      try {
        const [swmsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/swms/${project.id}`),
          axios.get(`${API}/settings/company-brand`),
        ]);
        setSwmsData({ ...EMPTY_SWMS, ...swmsRes.data });
        setCompanyInfo(settingsRes.data);
      } catch (err) {
        console.error('SWMSTab load error:', err);
        setSwmsData({ ...EMPTY_SWMS });
        setCompanyInfo({});
      }
    };
    load();
  }, [project?.id]);

  // Debounced autosave
  const scheduleAutosave = useCallback((patch) => {
    pendingPatch.current = { ...pendingPatch.current, ...patch };
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const toSave = { ...pendingPatch.current };
      pendingPatch.current = {};
      setSaving(true);
      setSaveError(null);
      try {
        await axios.patch(`${API}/swms/${project.id}`, toSave);
      } catch (err) {
        console.error('SWMS autosave error:', err);
        setSaveError('Autosave failed');
      } finally {
        setSaving(false);
      }
    }, 1000);
  }, [project?.id]);

  // Field change handler
  const handleChange = useCallback((field, value) => {
    setSwmsData(prev => ({ ...prev, [field]: value }));
    scheduleAutosave({ [field]: value });
  }, [scheduleAutosave]);

  if (!swmsData) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400">
        Loading SWMS…
      </div>
    );
  }

  const admin = isAdmin();

  return (
    <div className="space-y-6">
      {/* Status bar */}
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>Safe Work Method Statement</span>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPdfExport(true)}
            className="px-3 py-1 bg-[var(--theme-accent)] text-white rounded text-xs font-medium hover:opacity-90 transition-opacity"
          >
            Export PDF
          </button>
          <span>{saving ? 'Saving…' : saveError ? saveError : 'All changes saved'}</span>
        </div>
      </div>

      {/* ── Cover Page ── */}
      <ChapterCover
        projectInfo={derivedProject}
        companyName={companyInfo?.name || ''}
        swmsData={swmsData}
      />

      {/* ── Contents (Table of Contents) ── */}
      <ChapterContent companyName={companyInfo?.name || ''} />

      {/* ── Ch001: Document Control ── */}
      <Chapter001
        swmsData={swmsData}
        onChange={handleChange}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch002: Project Details & Introduction ── */}
      <Chapter002
        projectInfo={derivedProject}
        companyInfo={companyInfo}
        swmsData={swmsData}
        isAdmin={admin}
        onChange={handleChange}
      />

      {/* Section: SWMS Details */}
      <Section title="SWMS Details">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Scope of Works" value={swmsData.scope_of_works}
            onChange={v => handleChange('scope_of_works', v)} multiline />
          <Field label="Principal Contractor" value={swmsData.principal_contractor}
            onChange={v => handleChange('principal_contractor', v)} />
          <Field label="Site Manager Name" value={swmsData.site_manager_name}
            onChange={v => handleChange('site_manager_name', v)} />
          <Field label="Site Manager Phone" value={swmsData.site_manager_phone}
            onChange={v => handleChange('site_manager_phone', v)} />
          <Field label="Site Manager Email" value={swmsData.site_manager_email}
            onChange={v => handleChange('site_manager_email', v)} />
          <Field label="Number of Workers" value={String(swmsData.number_of_workers)}
            onChange={v => handleChange('number_of_workers', parseInt(v, 10) || 0)} />
        </div>
      </Section>

      {/* ── Ch003: OHSE & Environment Policy ── */}
      <Chapter003
        data={swmsData.ohse_policy}
        onChange={policy => handleChange('ohse_policy', policy)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch004: Hazard Identification Policy (static) ── */}
      <Chapter004 companyName={companyInfo?.name || ''} />

      {/* ── Ch005: Hazard Categories ── */}
      <Section title="Hazard Categories (Ch005)">
        <Chapter005
          data={swmsData.hazard_categories}
          onChange={cats => handleChange('hazard_categories', cats)}
          companyName={companyInfo?.name || ''}
        />
      </Section>

      {/* ── Ch006: Risk Matrix (static) ── */}
      <Chapter006 companyName={companyInfo?.name || ''} />

      {/* ── Ch007: Safe Work Method Statement ── */}
      <Section title="Safe Work Method Statement (Ch007)">
        <Chapter007
          swmsData={swmsData}
          onChange={handleChange}
          companyInfo={companyInfo || {}}
          projectInfo={derivedProject}
        />
      </Section>

      {/* ── Ch008: Objectives & Targets ── */}
      <Chapter008
        data={swmsData.objectives_targets}
        onChange={entries => handleChange('objectives_targets', entries)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch009: PPE Requirements ── */}
      <Section title="PPE Requirements (Ch009)">
        <Chapter009
          data={swmsData.ppe}
          onChange={ppe => handleChange('ppe', ppe)}
        />
      </Section>

      {/* ── Ch010: Roles & Responsibilities ── */}
      <Chapter010
        roles={swmsData.roles_responsibilities}
        onRolesChange={roles => handleChange('roles_responsibilities', roles)}
        employeeResponsibilities={swmsData.employee_responsibilities}
        onEmployeeChange={list => handleChange('employee_responsibilities', list)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch011: Training & Competency Register ── */}
      <Chapter011
        data={swmsData.training_entries}
        onChange={entries => handleChange('training_entries', entries)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch012: Consultation & Communication (static) ── */}
      <Chapter012 companyName={companyInfo?.name || ''} />

      {/* ── Ch013: Toolbox Talks ── */}
      <Chapter013
        data={swmsData.toolbox_talks}
        onChange={talks => handleChange('toolbox_talks', talks)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch014: Workplace Inspections ── */}
      <Chapter014
        data={swmsData.workplace_inspections}
        onChange={inspections => handleChange('workplace_inspections', inspections)}
        companyName={companyInfo?.name || ''}
        projectName={derivedProject.project_name}
      />

      {/* ── Ch015: Plant & Equipment Policy (static) ── */}
      <Chapter015 companyName={companyInfo?.name || ''} />

      {/* ── Ch016: Plant & Equipment Register ── */}
      <Chapter016
        data={swmsData.equipment_entries}
        onChange={entries => handleChange('equipment_entries', entries)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch017: Plant & Equipment Pre-Start Checklist ── */}
      <Chapter017
        data={swmsData.equipment_prestart_check}
        onChange={check => handleChange('equipment_prestart_check', check)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch018: Plant & Equipment Regular Checklist ── */}
      <Chapter018
        data={swmsData.equipment_periodic_check}
        onChange={check => handleChange('equipment_periodic_check', check)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch019: Hazardous Substances / DG Policy (static) ── */}
      <Chapter019 companyName={companyInfo?.name || ''} />

      {/* ── Ch020: Hazardous Substance Register ── */}
      <Chapter020
        data={swmsData.hazardous_substance_register}
        onChange={entries => handleChange('hazardous_substance_register', entries)}
      />

      {/* ── Ch021: Electrical Equipment Policy (static) ── */}
      <Chapter021 companyName={companyInfo?.name || ''} />

      {/* ── Ch022: Electrical Equipment Register ── */}
      <Chapter022
        data={swmsData.electrical_equipment_register}
        onChange={entries => handleChange('electrical_equipment_register', entries)}
        companyName={companyInfo?.name || ''}
        projectName={derivedProject.project_name}
      />

      {/* ── Ch023: Hazard Reporting (static) ── */}
      <Chapter023 companyName={companyInfo?.name || ''} />

      {/* ── Ch024: Hazard Report Form ── */}
      <Chapter024
        data={swmsData.hazard_reports}
        onChange={entries => handleChange('hazard_reports', entries)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch025: Incident & Injury Investigation Policy (static) ── */}
      <Chapter025 companyName={companyInfo?.name || ''} />

      {/* ── Ch026: Register of Injuries ── */}
      <Chapter026
        data={swmsData.injury_register}
        onChange={entries => handleChange('injury_register', entries)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch027: Incident Investigation Report ── */}
      <Chapter027
        data={swmsData.incident_investigations}
        onChange={entries => handleChange('incident_investigations', entries)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch028: OHSE Management Plan Checklist ── */}
      <Chapter028
        data={swmsData.ohse_checklist}
        onChange={checklist => handleChange('ohse_checklist', checklist)}
        companyName={companyInfo?.name || ''}
        projectName={derivedProject.project_name}
      />

      {/* ── Ch029: Injury Management & Return to Work ── */}
      <Chapter029
        data={swmsData.injury_management_policy}
        onChange={policy => handleChange('injury_management_policy', policy)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch030: Daily Sign In / Sign Out ── */}
      <Chapter030
        data={swmsData.signin_entries}
        onChange={entries => handleChange('signin_entries', entries)}
        companyName={companyInfo?.name || ''}
      />

      {/* ── Ch031: Site Rules & Emergency Contacts ── */}
      <Chapter031 companyName={companyInfo?.name || ''} />

      {/* Hidden PDF preview (always rendered but off-screen for html2canvas capture) */}
      <div style={{ position: 'absolute', left: -9999, top: 0, pointerEvents: 'none' }}>
        <SwmsPdfPreview
          swmsData={swmsData}
          projectInfo={derivedProject}
          companyInfo={companyInfo}
        />
      </div>

      {/* PDF Export Modal */}
      <SwmsPdfExportModal
        open={showPdfExport}
        onClose={() => setShowPdfExport(false)}
        projectName={derivedProject.project_name}
      />
    </div>
  );
}

// ── Small shared sub-components ───────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div className="bg-[var(--theme-bg-primary)] rounded-lg border border-[var(--theme-border)] p-4">
      <h3 className="text-sm font-semibold text-[var(--theme-text-primary)] mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, multiline = false }) {
  const cls = "w-full bg-[var(--theme-bg-secondary)] border border-[var(--theme-border)] rounded px-2 py-1 text-sm text-[var(--theme-text-primary)] focus:outline-none focus:border-[var(--theme-accent)]";
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      {multiline
        ? <textarea className={cls} rows={3} value={value} onChange={e => onChange(e.target.value)} />
        : <input className={cls} value={value} onChange={e => onChange(e.target.value)} />
      }
    </div>
  );
}
